import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { useMoveDeal } from '@/features/board/useBoard'
import NextContactDatePrompt from '@/components/NextContactDatePrompt'
import StageRequirementsDialog from '@/components/StageRequirementsDialog'
import { useFieldDefinitions } from '@/features/contacts/useContacts'
import { missingRequirements, type Requirement } from '@/lib/stageRequirements'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import type { MoveFills } from '@/api/deals'
import type { Deal, PipelineDetail, Stage } from '@/api/types'

/**
 * The deal's stage as a control, not a label: pick the next stage right from the deal (the board's
 * drag-and-drop stays as the other way). A native select so a phone shows its own picker; stages are
 * grouped as on the board, with won/lost last under "Close the deal". Moving to an open stage asks for
 * the next contact date, exactly like a drop on the board.
 */
export default function StagePicker({ deal, pipeline, stage }: { deal: Deal; pipeline: PipelineDetail | undefined; stage: Stage | null }) {
  const move = useMoveDeal(deal.pipelineId)
  const [promptFor, setPromptFor] = useState<Deal | null>(null)
  const { data: dealFields } = useFieldDefinitions('deal')
  // A stage that requires fields this deal lacks asks for them first (same as a board drop).
  const [gated, setGated] = useState<{ stage: Stage; missing: Requirement[] } | null>(null)
  if (!pipeline) return null

  const terminal = (s: Stage) => s.stageType === 'won' || s.stageType === 'lost'
  const groups = [
    ...pipeline.sections.map((sec) => ({ label: sec.name, stages: sec.stages.filter((s) => !terminal(s)) })),
    { label: 'Other stages', stages: pipeline.looseStages.filter((s) => !terminal(s)) },
    {
      label: 'Close the deal',
      stages: [...pipeline.sections.flatMap((sec) => sec.stages), ...pipeline.looseStages].filter(terminal),
    },
  ].filter((g) => g.stages.length > 0)
  const allStages = groups.flatMap((g) => g.stages)

  function onPick(stageId: string) {
    if (stageId === deal.stageId) return
    const target = allStages.find((s) => s.id === stageId)
    const missing = target ? missingRequirements(target, deal, dealFields ?? []) : []
    if (target && missing.length > 0) {
      move.reset()
      setGated({ stage: target, missing })
      return
    }
    doMove(stageId)
  }

  function doMove(stageId: string, fills?: MoveFills) {
    const target = allStages.find((s) => s.id === stageId)
    move.mutate(
      { dealId: deal.id, stageId, fills },
      {
        onSuccess: () => {
          setGated(null)
          // a date just entered for the stage's requirements needs no second ask
          if (target && !terminal(target) && !fills?.nextContactDate) setPromptFor({ ...deal, stageId })
        },
      },
    )
  }

  return (
    <>
      <span className="relative inline-flex items-center">
        <span
          className="absolute left-2.5 w-2 h-2 rounded-full pointer-events-none"
          style={{ backgroundColor: stage?.color ?? '#0ea5e9' }}
        />
        <select
          value={deal.stageId}
          onChange={(e) => onPick(e.target.value)}
          disabled={move.isPending}
          aria-label="Deal stage"
          title="Change the stage"
          className="appearance-none cursor-pointer rounded-full border border-gray-200 bg-white pl-6 pr-7 py-1 text-xs font-medium text-brand-dark hover:border-brand-primary/50 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-primary/40 disabled:opacity-60"
        >
          {groups.map((g) => (
            <optgroup key={g.label} label={g.label}>
              {g.stages.map((s) => (
                <option key={s.id} value={s.id}>
                  {terminal(s) && !/won|lost/i.test(s.name) ? `${s.stageType === 'won' ? 'Won' : 'Lost'} — ${s.name}` : s.name}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        <ChevronDown size={12} className="absolute right-2.5 text-gray-400 pointer-events-none" />
      </span>
      {move.isError && !gated && <span className="text-[11px] text-danger ml-2">{apiErrorMessage(move.error)}</span>}
      {gated && (
        <StageRequirementsDialog
          deal={deal}
          stage={gated.stage}
          missing={gated.missing}
          pending={move.isPending}
          error={move.isError ? apiErrorMessage(move.error) : null}
          onMove={(fills) => doMove(gated.stage.id, fills)}
          onClose={() => setGated(null)}
        />
      )}
      {promptFor && <NextContactDatePrompt deal={promptFor} pipelineId={deal.pipelineId} onClose={() => setPromptFor(null)} />}
    </>
  )
}
